import React from 'react';
import { ShieldCheck } from 'lucide-react';
import { colors, borderRadius } from '../../styles/tokens';
import { useBlockEditor } from '../../hooks/useBlockEditor';
import { EditableField } from '../editing';

const CURRENCY_OPTIONS = [
  { value: 'USD', label: 'USD' },
  { value: 'EUR', label: 'EUR' },
  { value: 'BRL', label: 'BRL' },
];

export default function InsuranceCard({ block, onRemove, quoteId, onBlockUpdate }) {
  const editable = !!quoteId;
  const { saving, updateField, updateDetail, isSaved } =
    useBlockEditor(quoteId, block, onBlockUpdate);

  const details = block.details || {};

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr + 'T00:00:00').toLocaleDateString('pt-BR');
  };

  const formatCoverage = () => {
    const amount = details.coverage_amount || 0;
    const currency = details.coverage_currency || 'USD';
    return `${currency} ${amount.toLocaleString('pt-BR')}`;
  };

  const E = (value, onSave, opts = {}) => {
    if (!editable) return opts.fallback || value || opts.placeholder || '-';
    return (
      <EditableField
        value={value}
        onSave={onSave}
        type={opts.type || 'text'}
        options={opts.options}
        saving={saving}
        saved={opts.saved}
        placeholder={opts.placeholder || '-'}
        style={opts.style}
        validate={opts.validate}
      />
    );
  };

  return (
    <div style={{
      backgroundColor: colors.card, border: `1px solid ${colors.border}`,
      borderRadius: borderRadius.md, padding: '1.5rem', position: 'relative',
    }}>
      <button onClick={() => onRemove(block.id)} style={{
        position: 'absolute', top: '1rem', right: '1rem',
        background: 'none', border: 'none', cursor: 'pointer',
        color: colors.text.muted, fontSize: '1.25rem',
      }}>×</button>

      <div style={{ display: 'flex', gap: '1rem' }}>
        <div style={{
          width: '48px', height: '48px', backgroundColor: `${colors.primary}10`,
          borderRadius: borderRadius.sm, display: 'flex', alignItems: 'center',
          justifyContent: 'center', flexShrink: 0,
        }}>
          <ShieldCheck size={24} color={colors.primary} />
        </div>

        <div style={{ flex: 1 }}>
          <h4 style={{ fontSize: '1.125rem', fontWeight: 600, color: colors.primary, margin: '0 0 1rem 0' }}>
            {E(block.title, (v) => updateField('title', v), { saved: isSaved('title'), fallback: block.title })}
          </h4>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
            {/* Seguradora */}
            <div>
              <p style={{ fontSize: '0.75rem', color: colors.text.muted, marginBottom: '0.25rem' }}>Seguradora</p>
              <p style={{ fontSize: '0.875rem', fontWeight: 600, color: colors.text.primary, margin: 0 }}>
                {E(details.company, (v) => updateDetail('company', v), {
                  saved: isSaved('company'), fallback: details.company || '-',
                })}
              </p>
            </div>

            {/* Cobertura */}
            <div>
              <p style={{ fontSize: '0.75rem', color: colors.text.muted, marginBottom: '0.25rem' }}>Cobertura</p>
              <p style={{ fontSize: '0.875rem', fontWeight: 600, color: colors.text.primary, margin: 0 }}>
                {editable ? (
                  <>
                    <EditableField
                      value={details.coverage_currency || 'USD'}
                      onSave={(v) => updateDetail('coverage_currency', v)}
                      type="select"
                      options={CURRENCY_OPTIONS}
                      saving={saving}
                      saved={isSaved('coverage_currency')}
                    />
                    {' '}
                    <EditableField
                      value={details.coverage_amount || 0}
                      onSave={(v) => updateDetail('coverage_amount', v)}
                      type="number"
                      saving={saving}
                      saved={isSaved('coverage_amount')}
                    />
                  </>
                ) : formatCoverage()}
              </p>
            </div>

            {/* Início */}
            <div>
              <p style={{ fontSize: '0.75rem', color: colors.text.muted, marginBottom: '0.25rem' }}>Início</p>
              <p style={{ fontSize: '0.875rem', fontWeight: 600, color: colors.text.primary, margin: 0 }}>
                {E(details.start_date || block.date, (v) => updateDetail('start_date', v), {
                  type: 'date', saved: isSaved('start_date'),
                  fallback: formatDate(details.start_date || block.date),
                })}
              </p>
            </div>

            {/* Fim */}
            <div>
              <p style={{ fontSize: '0.75rem', color: colors.text.muted, marginBottom: '0.25rem' }}>Fim</p>
              <p style={{ fontSize: '0.875rem', fontWeight: 600, color: colors.text.primary, margin: 0 }}>
                {E(details.end_date, (v) => updateDetail('end_date', v), {
                  type: 'date', saved: isSaved('end_date'),
                  fallback: formatDate(details.end_date),
                })}
              </p>
            </div>

            {/* Passageiros */}
            {(details.passengers || editable) && (
              <div>
                <p style={{ fontSize: '0.75rem', color: colors.text.muted, marginBottom: '0.25rem' }}>Passageiros</p>
                <p style={{ fontSize: '0.875rem', fontWeight: 600, color: colors.text.primary, margin: 0 }}>
                  {E(details.passengers, (v) => updateDetail('passengers', parseInt(v) || 0), {
                    type: 'number', saved: isSaved('passengers'),
                    fallback: details.passengers || '-',
                  })}
                </p>
              </div>
            )}

            {/* Valor */}
            <div style={{ textAlign: 'right' }}>
              <p style={{ fontSize: '0.75rem', color: colors.text.muted, marginBottom: '0.25rem' }}>Valor</p>
              <p style={{ fontSize: '1.25rem', fontWeight: 700, color: colors.accent, margin: 0 }}>
                {E(block.price || 0, (v) => updateField('price', v), {
                  type: 'currency', saved: isSaved('price'),
                  validate: (v) => v < 0 ? 'Preço inválido' : null,
                  fallback: `R$ ${(block.price || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`,
                })}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
